<template>
    <EChart
        :options="chartOptions"
        card
        :header="$t('averageAvailabilityByProducts')"
        overlay
        :loading="request.getLoading() || requestCompare.getLoading()"
        :error="request.getError()"
        :tooltip="$t('tooltip')"
    >
        <template #nav>
            <div class="d-flex gap-3 flex-column">
                <div class="d-flex gap-3">
                    <DropdownFilter v-model="currentNumberShown" :items="[10, 20, 50, null]" :prefix="`${$t('show')}: `" :disabled="loading" />
                    <DropdownFilter v-model="currentSortBy" :items="['averageDesc', 'averageAsc', 'changeDesc', 'changeAsc']" :prefix="`${$t('sortBy')}: `" :disabled="loading" />
                </div>
                <div class="ml-auto d-flex gap-3 justify-content-end align-items-center">
                    <b-form-input v-model="filterSearch" class="width-200" :placeholder="$t('search')" :disabled="loading" />
                </div>
            </div>
        </template>
    </EChart>
</template>



<script>
    import sum from 'lodash/sum';
    import camelCase from 'camelcase';
    import { calcPercentDiff } from '@/utils/number';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'AverageAvailabilityByProducts',
        components: {
            EChart,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                currentNumberShown: 10,
                currentSortBy: 'averageDesc',
                filterSearch: null,
            };
        },
        computed: {
            loading() {
                return this.request.getLoading();
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            filteredChartData() {
                if (this.filterSearch) {
                    return this.chartData.filter(item => item.product.toLowerCase().includes(this.filterSearch.toLowerCase()));
                }
                return this.chartData;
            },
            sortedChartData() {
                const compareDict = {};
                this.chartDataCompare.forEach(item => {
                    compareDict[item.product] = item.avgAvailability;
                });
                const baseData = this.filteredChartData;
                const dataWithDiff = baseData.map(item => {
                    const compareValue = compareDict[item.product] || 0;
                    const diff = calcPercentDiff(item.avgAvailability, compareValue);
                    return { ...item, diff };
                });
                let sorted;
                switch (this.currentSortBy) {
                    case 'averageDesc':
                        sorted = [...dataWithDiff].sort((a, b) => b.avgAvailability - a.avgAvailability);
                        break;
                    case 'averageAsc':
                        sorted = [...dataWithDiff].sort((a, b) => a.avgAvailability - b.avgAvailability);
                        break;
                    case 'changeDesc':
                        sorted = [...dataWithDiff].sort((a, b) => b.diff - a.diff);
                        break;
                    case 'changeAsc':
                        sorted = [...dataWithDiff].sort((a, b) => a.diff - b.diff);
                        break;
                    default:
                        sorted = dataWithDiff;
                }
                return this.currentNumberShown ? sorted.slice(0, this.currentNumberShown) : sorted;
            },
            chartOptions() {
                // guard na brak danych
                if (!this.sortedChartData.length) {
                    return { series: [] };
                }

                // przygotowanie tablic
                const names = this.sortedChartData.map(item => item.product);
                const values = this.sortedChartData.map(item => item.avgAvailability);
                const diffs = this.sortedChartData.map(item => item.diff);
                const summary = sum(values);

                return {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // lewa oś z nazwami produktów
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // prawa oś z procentową zmianą
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const diff = diffs[idx];
                                    const pct = this.$format.percent(diff);
                                    if (diff > 0) return `{pos|▲${pct}}`;
                                    if (diff < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykieta wewnątrz słupka: udział % + wartość
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => {
                                    const value = params.data.value;
                                    return `${this.$format.percent((value * 100) / summary, 2)} (${value})`;
                                },
                            },
                            data: values.map(value => ({ value })),
                        },
                    ],
                };
            },
        },
        methods: {
            pascalCase(text) {
                return camelCase(text, { pascalCase: true });
            },
            computeChartData(source) {
                /* eslint-disable no-restricted-syntax, guard-for-in */
                const eventsByProduct = {};
                Object.values(source).forEach(productObj => {
                    const productName = productObj.product.name;
                    if (!eventsByProduct[productName]) {
                        eventsByProduct[productName] = [];
                    }
                    const charts = productObj.charts;
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const segments = chartObj.chart.segments;
                            if (segments && segments.length) {
                                segments.forEach(segment => {
                                    eventsByProduct[productName].push({ time: segment.start, delta: +1 });
                                    eventsByProduct[productName].push({ time: segment.end, delta: -1 });
                                });
                            }
                        });
                    });
                });
                const productSummaries = [];
                for (const product in eventsByProduct) {
                    const events = eventsByProduct[product];
                    const grouped = {};
                    events.forEach(e => {
                        grouped[e.time] = (grouped[e.time] || 0) + e.delta;
                    });
                    const times = Object.keys(grouped)
                        .map(t => Number(t))
                        .sort((a, b) => a - b);
                    if (times.length === 0) continue; // eslint-disable-line no-continue
                    let cumulative = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let prevTime = times[0];
                    times.forEach(time => {
                        const duration = time - prevTime;
                        weightedSum += cumulative * duration;
                        totalDuration += duration;
                        cumulative += grouped[time];
                        prevTime = time;
                    });
                    const avg = totalDuration ? weightedSum / totalDuration : cumulative;
                    productSummaries.push({
                        product,
                        avgAvailability: Math.round(avg),
                    });
                }
                productSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return productSummaries;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Wykres prezentuje ranking produktów według średniej dostępności ofert produktów producenta w sklepach wyświetlanych w widgecie (ustawienia domyślnie). Aby sprawdzić liczbę ofert w pozostałych sklepach, użyj filtra Sklep."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The chart presents the ranking of products according to the average availability of manufacturer's products offers in stores displayed in the widget (settings by default). To check the number of offers in other stores, use the shop filter."
}
</i18n>
